import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
} from "react";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@rook/shared/lib/cn";

/**
 * Tooltip — keyboard + screen-reader-friendly hover/focus hint.
 * Use anywhere we previously dropped a `title="..."` attribute on
 * a button or icon. `title` is hover-only, never appears on
 * keyboard focus, and screen readers handle it inconsistently —
 * Radix Tooltip is the modern fix.
 *
 * Wrap your app once in `<TooltipProvider>` (we mount it at
 * `main.tsx`); then any `<Tooltip>` works without extra context.
 *
 * Usage:
 *   <Tooltip>
 *     <TooltipTrigger asChild>
 *       <Button variant="ghost">…</Button>
 *     </TooltipTrigger>
 *     <TooltipContent>Editing rotates the SDK secret.</TooltipContent>
 *   </Tooltip>
 */
export const TooltipProvider = TooltipPrimitive.Provider;
export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 max-w-xs overflow-hidden rounded-md border border-white/10 bg-popover px-3 py-1.5 text-xs text-popover-foreground shadow-md",
        "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
